import { useEffect, useState } from "react";
import { DefaultButton, IContextualMenuItem } from "@fluentui/react";

import { disconnectMcpServerApiV2, listMcpConnectionsApiV2, McpConnection, startMcpAuthorizationApiV2 } from "../../api";
import styles from "./McpConnections.module.css";

// Sent by the backend callback page (see _mcp_authorization_result_page in v2_routes.py).
const AUTHORIZATION_CHANNEL = "mcp-oauth";

type AuthorizationResult = {
    serverKey: string | null;
    success: boolean;
};

interface Props {
    token: string;
    className?: string;
}

export const McpConnections = ({ token, className }: Props) => {
    const [connections, setConnections] = useState<McpConnection[]>([]);
    const [statusMessage, setStatusMessage] = useState<string>("");

    const loadConnections = async () => {
        if (!token) return;
        try {
            setConnections(await listMcpConnectionsApiV2(token));
        } catch (e) {
            console.error("Failed to load MCP connections:", e);
        }
    };

    useEffect(() => {
        loadConnections();

        const channel = new BroadcastChannel(AUTHORIZATION_CHANNEL);
        channel.onmessage = (event: MessageEvent<AuthorizationResult>) => {
            setStatusMessage(event.data.success ? "" : "Authorization failed. Please try again.");
            loadConnections();
        };
        return () => channel.close();
    }, [token]);

    const connect = async (serverKey: string) => {
        setStatusMessage("");
        // Opened before the await: browsers block popups not opened directly from the click.
        const popup = window.open("", AUTHORIZATION_CHANNEL, "width=520,height=680");
        if (!popup) {
            setStatusMessage("Please allow popups for this site to connect.");
            return;
        }
        try {
            popup.location.href = await startMcpAuthorizationApiV2(serverKey, token);
        } catch (e) {
            console.error("Failed to start MCP authorization:", e);
            popup.close();
            setStatusMessage("Could not start the authorization.");
        }
    };

    const disconnect = async (serverKey: string) => {
        setStatusMessage("");
        try {
            await disconnectMcpServerApiV2(serverKey, token);
        } catch (e) {
            console.error("Failed to disconnect MCP server:", e);
            setStatusMessage("Could not disconnect.");
        }
        loadConnections();
    };

    if (connections.length === 0) return null;

    const connectedCount = connections.filter(connection => connection.connected).length;
    const menuItems: IContextualMenuItem[] = connections.flatMap(connection => {
        const connectItem: IContextualMenuItem = {
            key: connection.key,
            text: connection.connected ? `${connection.display_name} (connected)` : `Connect ${connection.display_name}`,
            title: connection.connected ? "Connected. Click to log in again." : "Log in to use this tool",
            iconProps: { iconName: connection.connected ? "PlugConnected" : "PlugDisconnected" },
            onClick: () => {
                connect(connection.key);
            }
        };
        if (!connection.connected) return [connectItem];
        const disconnectItem: IContextualMenuItem = {
            key: `${connection.key}-disconnect`,
            text: `Disconnect ${connection.display_name}`,
            iconProps: { iconName: "PlugDisconnected" },
            onClick: () => {
                disconnect(connection.key);
            }
        };
        return [connectItem, disconnectItem];
    });

    return (
        <div className={`${styles.container} ${className ?? ""}`}>
            <DefaultButton
                text={`MCP connections (${connectedCount}/${connections.length})`}
                iconProps={{ iconName: connectedCount > 0 ? "PlugConnected" : "PlugDisconnected" }}
                menuProps={{ items: menuItems }}
            />
            {statusMessage && <span className={styles.status}>{statusMessage}</span>}
        </div>
    );
};
